import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import API, { formatError } from "@/api";
import {
    LayoutDashboard, Building2, BarChart3,
    Bell, Settings, ShieldCheck, ChevronLeft, ChevronRight,
    LogOut, Plus, Menu, X, CalendarDays, MessageSquare,
    Sun, Moon, HandHelping, ListChecks
} from "lucide-react";
import { toast } from "sonner";
import { displayRole } from "@/lib/roles";
import { requestPermission, getPermission, isSupported as isNotifSupported, isBannerSuppressed, suppressBanner, showNotification } from "@/lib/browserNotifications";

function TomBrand({ collapsed }) {
    return (
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2"}`}>
            <div className="w-9 h-9 rounded-md bg-[#0A0A0F] flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden">
                <img src="/logo-tom-v3.png" alt="TOM" className="w-full h-full object-contain p-0.5" />
            </div>
            {!collapsed && (
                <div className="leading-tight">
                    <div className="font-extrabold text-[#111827] dark:text-white text-[15px] tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>TOM</div>
                    <div className="text-[10px] text-[#9CA3AF] dark:text-[#7E858E] uppercase tracking-wider font-semibold">Team Ops Mgr</div>
                </div>
            )}
        </div>
    );
}

export default function AppShell() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [workspaces, setWorkspaces] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingAssistance, setPendingAssistance] = useState(0);
    const [unreadChat, setUnreadChat] = useState(0);
    const [showCreateWs, setShowCreateWs] = useState(false);
    const [wsForm, setWsForm] = useState({ name: "", description: "" });
    const [savingWs, setSavingWs] = useState(false);
    // Pentru browser-notifications: păstrăm valorile anterioare
    const prevUnreadRef = useRef({ notif: 0, chat: 0, initialized: false });

    const isAdmin = ["super_admin", "admin"].includes(user?.role);

    const fetchWorkspaces = () => {
        API.get("/workspaces").then(r => setWorkspaces(r.data)).catch(() => {});
    };

    const fetchCounters = async () => {
        let newNotif = 0;
        let newChat = 0;
        let latestNotifTitle = null;
        try {
            const r = await API.get("/notifications/count");
            newNotif = r.data.count || 0;
        } catch { /* ignore */ }
        try {
            const r = await API.get("/chat/channels");
            newChat = (r.data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
        } catch { /* ignore */ }
        try {
            const r = await API.get("/assistance/pending");
            setPendingAssistance(r.data.length || 0);
        } catch { /* ignore */ }

        // Browser push: la creșterea contoarelor (după prima initializare)
        const prev = prevUnreadRef.current;
        if (prev.initialized) {
            if (newNotif > prev.notif) {
                // Încarcă cea mai nouă notificare pentru a o afișa
                try {
                    const r = await API.get("/notifications");
                    const latest = (r.data || [])[0];
                    if (latest) {
                        latestNotifTitle = latest.title || "Notificare nouă";
                        showNotification(latestNotifTitle, {
                            body: latest.body || "",
                            tag: `tom-notif-${latest.id}`,
                            onClick: () => navigate("/notifications"),
                        });
                    } else {
                        showNotification("Ai o notificare nouă", { tag: "tom-notif", onClick: () => navigate("/notifications") });
                    }
                } catch {
                    showNotification("Ai o notificare nouă", { tag: "tom-notif", onClick: () => navigate("/notifications") });
                }
            }
            if (newChat > prev.chat) {
                showNotification("Mesaj nou în chat", {
                    body: `Ai ${newChat} mesaj${newChat === 1 ? "" : "e"} necitit${newChat === 1 ? "" : "e"}.`,
                    tag: "tom-chat",
                    onClick: () => navigate("/chat"),
                });
            }
        }
        prevUnreadRef.current = { notif: newNotif, chat: newChat, initialized: true };
        setUnreadCount(newNotif);
        setUnreadChat(newChat);
    };

    const [notifPermission, setNotifPermission] = useState(() => (isNotifSupported() ? Notification.permission : "unsupported"));
    const [showNotifBanner, setShowNotifBanner] = useState(() => {
        if (!isNotifSupported()) return false;
        if (Notification.permission !== "default") return false;
        return !isBannerSuppressed();
    });

    useEffect(() => {
        fetchWorkspaces();
        fetchCounters();
        const interval = setInterval(fetchCounters, 30000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enableBrowserNotifications = async () => {
        const perm = await requestPermission();
        setNotifPermission(perm);
        if (perm === "granted") {
            toast.success("Notificările desktop sunt activate. Vei primi alerte chiar și când TOM nu e tab-ul activ.");
            // Test notification ca să vadă utilizatorul cum arată
            showNotification("TOM — notificările sunt active!", {
                body: "Vei primi alerte pentru taskuri noi, mesaje și mutări în Stand By.",
                tag: "tom-test",
                force: true,
            });
            setShowNotifBanner(false);
        } else if (perm === "denied") {
            toast.error("Browser-ul a blocat notificările. Activează-le manual din setările site-ului (icon-ul de lacăt din bara de adresă).");
            setShowNotifBanner(false);
        } else {
            // default - user a închis dialogul fără să decidă
            toast.message("Decizia de notificări a fost amânată. Revino oricând din banner.");
        }
    };

    const dismissNotifBanner = () => {
        suppressBanner();
        setShowNotifBanner(false);
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
        toast.success("Deconectat cu succes");
    };

    const createWorkspace = async (e) => {
        e.preventDefault();
        setSavingWs(true);
        try {
            const res = await API.post("/workspaces", wsForm);
            toast.success("Workspace creat");
            setShowCreateWs(false);
            setWsForm({ name: "", description: "" });
            fetchWorkspaces();
            navigate(`/workspaces/${res.data.id}`);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSavingWs(false);
        }
    };

    const navItems = [
        { icon: <LayoutDashboard size={18} />, label: "Dashboard", path: "/" },
        { icon: <CalendarDays size={18} />, label: "Calendar", path: "/calendar" },
        { icon: <MessageSquare size={18} />, label: "Chat", path: "/chat", badge: unreadChat },
        { icon: <HandHelping size={18} />, label: "Asistență", path: "/assistance", badge: pendingAssistance },
        { icon: <BarChart3 size={18} />, label: "Rapoarte", path: "/reports" },
        { icon: <Bell size={18} />, label: "Notificări", path: "/notifications", badge: unreadCount },
        { icon: <Settings size={18} />, label: "Setări", path: "/settings" },
    ];

    if (isAdmin) {
        navItems.push({ icon: <ListChecks size={18} />, label: "Toate taskurile", path: "/admin/tasks" });
        navItems.push({ icon: <ShieldCheck size={18} />, label: "Manager Departament", path: "/admin" });
    }

    const isActive = (path) => {
        if (path === "/") return location.pathname === "/";
        return location.pathname.startsWith(path);
    };

    return (
        <div className="flex h-screen bg-[#F8F9FA] dark:bg-[#0B0D10] overflow-hidden" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {mobileOpen && (
                <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
                flex flex-col bg-white dark:bg-[#0F1317] border-r border-[#E4E4E7] dark:border-[#1E232B]
                transition-all duration-200 ease-in-out
                ${collapsed ? "w-16" : "w-60"}
                ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            `}>
                {/* Logo */}
                <div className={`flex items-center gap-2 px-4 h-14 border-b border-[#E4E4E7] dark:border-[#1E232B] flex-shrink-0 ${collapsed ? "justify-center px-2" : ""}`}>
                    <TomBrand collapsed={collapsed} />
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
                    {navItems.map((item) => (
                        <button key={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => { navigate(item.path); setMobileOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors relative
                                ${isActive(item.path) ? "bg-[#002FA7]/8 text-[#002FA7] font-semibold" : "text-[#4B5563] hover:bg-[#F1F3F5] hover:text-[#111827]"}
                                ${collapsed ? "justify-center" : ""}`}>
                            <span className="flex-shrink-0">{item.icon}</span>
                            {!collapsed && <span className="truncate">{item.label}</span>}
                            {!collapsed && item.badge > 0 && (
                                <span className="ml-auto bg-[#002FA7] text-white text-xs rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center flex-shrink-0">
                                    {item.badge > 99 ? "99+" : item.badge}
                                </span>
                            )}
                            {collapsed && item.badge > 0 && (
                                <span className="absolute top-1 right-1 bg-[#002FA7] text-white text-[10px] rounded-full w-3.5 h-3.5 flex items-center justify-center">
                                    {item.badge > 9 ? "9+" : item.badge}
                                </span>
                            )}
                        </button>
                    ))}

                    {/* Workspaces section */}
                    {!collapsed && (workspaces.length > 0 || isAdmin) && (
                        <div className="pt-3">
                            <div className="px-3 py-1 flex items-center justify-between">
                                <span className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest">Workspace-uri</span>
                                {isAdmin && (
                                    <button onClick={() => setShowCreateWs(true)} data-testid="create-workspace-btn"
                                        className="text-[#9CA3AF] hover:text-[#002FA7] hover:bg-[#EEF2FF] rounded p-0.5 transition-colors"
                                        title="Workspace nou">
                                        <Plus size={13} />
                                    </button>
                                )}
                            </div>
                            {workspaces.map(ws => (
                                <button key={ws.id} data-testid={`nav-workspace-${ws.id}`}
                                    onClick={() => { navigate(`/workspaces/${ws.id}`); setMobileOpen(false); }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors
                                        ${location.pathname === `/workspaces/${ws.id}` ? "bg-[#002FA7]/8 text-[#002FA7] font-semibold" : "text-[#4B5563] hover:bg-[#F1F3F5] hover:text-[#111827]"}`}>
                                    <Building2 size={16} className="flex-shrink-0" />
                                    <span className="truncate">{ws.name}</span>
                                </button>
                            ))}
                            {workspaces.length === 0 && isAdmin && (
                                <p className="text-xs text-[#C7C7C7] px-3 py-1.5 italic">Niciun workspace</p>
                            )}
                        </div>
                    )}
                </nav>

                {/* User & Collapse */}
                <div className="border-t border-[#E4E4E7] dark:border-[#1E232B] p-2 flex-shrink-0">
                    {!collapsed && (
                        <div className="flex items-center gap-2 px-2 py-2 mb-1">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: user?.avatar || "#002FA7" }}>
                                <span className="text-white text-xs font-bold">{user?.name?.[0]?.toUpperCase() || "U"}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{user?.nickname || user?.name}</p>
                                <p className="text-xs text-[#9CA3AF] truncate">{user?.job_title || displayRole(user)}</p>
                            </div>
                            <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 transition-colors" title="Deconectare" data-testid="logout-btn">
                                <LogOut size={15} />
                            </button>
                        </div>
                    )}
                    {collapsed && (
                        <button onClick={handleLogout} className="w-full flex justify-center py-2 text-gray-400 hover:text-red-500 transition-colors" data-testid="logout-btn-collapsed">
                            <LogOut size={15} />
                        </button>
                    )}
                    <button onClick={() => setCollapsed(!collapsed)}
                        className="hidden lg:flex w-full items-center justify-center py-1.5 text-gray-400 hover:text-gray-600 hover:bg-[#F1F3F5] rounded-md transition-colors"
                        data-testid="sidebar-collapse-btn">
                        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <header className="h-14 bg-white dark:bg-[#0F1317] border-b border-[#E4E4E7] dark:border-[#1E232B] flex items-center gap-4 px-4 flex-shrink-0 z-30">
                    <button className="lg:hidden text-gray-500" onClick={() => setMobileOpen(!mobileOpen)}>
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                    <div className="flex-1"></div>
                    <button onClick={toggle} data-testid="theme-toggle-btn"
                        className="text-[#4B5563] hover:text-[#002FA7] transition-colors p-1.5 rounded-md hover:bg-[#F1F3F5]"
                        title={theme === "dark" ? "Comută la temă deschisă" : "Comută la temă întunecată"}>
                        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    <button data-testid="topbar-notifications-btn" onClick={() => navigate("/notifications")}
                        className="relative text-[#4B5563] hover:text-[#002FA7] transition-colors p-1.5 rounded-md hover:bg-[#F1F3F5]">
                        <Bell size={18} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-[#002FA7] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                        )}
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: user?.avatar || "#002FA7" }}>
                            <span className="text-white text-xs font-bold">{user?.name?.[0]?.toUpperCase() || "U"}</span>
                        </div>
                        <span className="hidden sm:block text-sm text-[#4B5563] font-medium">{user?.nickname || user?.name}</span>
                    </div>
                </header>
                {showNotifBanner && (
                    <div data-testid="notif-banner" className="px-6 py-2.5 bg-gradient-to-r from-[#EEF2FF] to-[#FAFBFD] border-b border-[#C7D2FE] flex items-center gap-3">
                        <Bell size={15} className="text-[#002FA7] flex-shrink-0" />
                        <div className="flex-1 text-xs text-[#1F2937]">
                            <span className="font-semibold">Activează notificările desktop</span>
                            <span className="text-[#6B7280] ml-1.5">— primește alerte instant când ai task nou, mesaj sau ceva mutat în Stand By, chiar și când TOM nu e tab-ul activ.</span>
                        </div>
                        <button onClick={enableBrowserNotifications}
                            data-testid="enable-notifications-btn"
                            className="text-xs font-semibold bg-[#002FA7] text-white px-3 py-1 rounded hover:bg-[#002482] transition-colors">
                            Activează
                        </button>
                        <button onClick={dismissNotifBanner} className="text-xs text-[#6B7280] hover:text-[#111827] px-1">
                            Mai târziu
                        </button>
                    </div>
                )}
                <main className="flex-1 overflow-y-auto">
                    <Outlet />
                </main>
            </div>

            {/* Create Workspace Modal */}
            {showCreateWs && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateWs(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Workspace nou</h2>
                        </div>
                        <form onSubmit={createWorkspace} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="new-ws-name" value={wsForm.name}
                                    onChange={e => setWsForm({ ...wsForm, name: e.target.value })}
                                    placeholder="ex. Proiect Alpha, Marketing 2026..."
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] placeholder:text-[#D1D5DB]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                <input data-testid="new-ws-description" value={wsForm.description}
                                    onChange={e => setWsForm({ ...wsForm, description: e.target.value })}
                                    placeholder="Scurtă descriere..."
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] placeholder:text-[#D1D5DB]" />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowCreateWs(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="create-ws-submit" type="submit" disabled={savingWs}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {savingWs ? "Se creează..." : "Creează workspace"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
